import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * What is left of the fashe theme's jQuery bundle: sweetalert, the lightbox, and the header
 * behaviour in main.min.js. It is loaded once, after the first render.
 *
 * <p>Client-side navigation is the one thing the bundle knows nothing about: it binds to the
 * elements that existed when it ran. So every navigation after the first rebinds the dropdowns of
 * the page that just rendered - the header and footer bindings survive, since those elements are
 * never replaced.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {

  private static readonly SCRIPTS = [
    '/fashe/vendor/jquery/jquery-3.2.1.min.js',
    '/fashe/vendor/bootstrap/js/popper.js',
    '/fashe/vendor/bootstrap/js/bootstrap.min.js',
    '/fashe/vendor/sweetalert/sweetalert.min.js',
    '/fashe/vendor/lightbox2/js/lightbox.min.js',
    '/js/lazysizes.min.js',
    '/fashe/js/main.min.js'
  ];

  private document = inject(DOCUMENT);
  private loading?: Promise<void>;

  async refresh(): Promise<void> {
    if (!this.loading) {
      // A rejected promise kept here would be re-awaited by every later navigation, so one
      // failed script would leave the accordions and the lightbox dead for the whole session.
      this.loading = this.load().catch(error => {
        this.loading = undefined;
        throw error;
      });
      await this.loading;
      return;
    }
    // A navigation can land before the bundle has finished loading; the dropdowns need it.
    await this.loading;
    this.dropdowns();
  }

  private async load(): Promise<void> {
    for (const source of ThemeService.SCRIPTS) {
      await this.script(source);
    }
    this.lightbox();
    this.dropdowns();
  }

  private lightbox(): void {
    const global = this.document.defaultView as { lightbox?: { option(options: object): void } } | null;
    global?.lightbox?.option({ albumLabel: '%1 / %2' });
  }

  private script(source: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = source;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Cannot load ' + source));
      this.document.body.appendChild(script);
    });
  }

  /**
   * The accordions: the filter panel, and the three sections under a product.
   *
   * <p>Runs after the bundle loads and again after every navigation, so both halves have to be
   * safe to repeat - which is what they were not.
   *
   * <p>off('click') is deliberate and is the whole bug. main.min.js binds these headings too,
   * so a second handler left every click toggling twice: the section opened and shut inside one
   * click and looked like it would not open at all.
   *
   * <p>The open state is SET from the markup rather than toggled, for the same reason. A toggle
   * closes on the second call what it opened on the first, which is why picking a filter - a
   * navigation - collapsed the panel that had just been opened.
   */
  private dropdowns(): void {
    if (typeof $ !== 'function') {
      return;
    }
    const headings = $('.js-toggle-dropdown-content');
    headings.off('click').on('click', event => {
      const heading = $(event.currentTarget);
      heading.toggleClass('show-dropdown-content');
      heading.parent().find('.dropdown-content').slideToggle('fast');
    });
    headings.each((index, element) => {
      const heading = $(element);
      const open = heading.parent().hasClass('active-dropdown-content');
      heading.toggleClass('show-dropdown-content', open);
      heading.parent().find('.dropdown-content').toggle(open);
    });
  }

}
